import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { chatbotAdminApi } from '../../api/chatbotApi';
import { ChatbotConversation, ChatbotMessage } from '../../types/chatbot.types';
import { format } from 'date-fns';
import { FileText, FileJson, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { useAppSelector } from '@/store/hooks';
import { selectCurrentUser } from '@/features/auth/authSlice';
import { compactChatbotInfoFields, padChatbotInfoFields, type ChatbotInfoField } from '@workspace/types';
import { InfoFieldsEditor } from './InfoFieldsEditor';
import { AuthenticatedMedia } from '../AuthenticatedMedia';

type ExportFormat = 'CSV' | 'JSON' | 'PDF';

interface ConversationViewerProps {
  instanceId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  canExport?: boolean;
}

function hasFileExtension(url: string, extensions: string[]): boolean {
  const boundedUrl = url.slice(0, 2048).toLowerCase();
  const path = boundedUrl.split('?')[0];
  return extensions.some(ext => path.endsWith(ext));
}

function getFileName(message: ChatbotMessage): string {
  const metadataName = message.fileMetadata?.name;
  if (typeof metadataName === 'string' && metadataName.trim()) {
    return metadataName;
  }
  return message.fileUrl?.split('/').pop() || 'Archivo adjunto';
}

function formatFileSize(size?: number): string | null {
  if (!size || size <= 0) return null;
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function getMetadataString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function getMetadataNumber(value: unknown): number | undefined {
  return typeof value === 'number' ? value : undefined;
}

function getMessageKind(message: ChatbotMessage): 'audio' | 'image' | 'file' | 'text' {
  if (!message.fileUrl) return 'text';
  const mimeType = getMetadataString(message.fileMetadata?.mimeType).toLowerCase();
  if (
    message.contentType === 'AUDIO' ||
    mimeType.startsWith('audio/') ||
    hasFileExtension(message.fileUrl, ['.mp3', '.wav', '.ogg', '.m4a', '.webm'])
  ) {
    return 'audio';
  }
  if (
    message.contentType === 'IMAGE' ||
    mimeType.startsWith('image/') ||
    hasFileExtension(message.fileUrl, ['.jpg', '.jpeg', '.png', '.gif', '.webp'])
  ) {
    return 'image';
  }
  return 'file';
}

function isAudioPlaceholderContent(message: ChatbotMessage, content: string): boolean {
  const normalizedContent = content.slice(0, 256).trim().toLowerCase();
  const fileName = getFileName(message).slice(0, 256).trim().toLowerCase();
  const fileUrlName = message.fileUrl?.split('/').pop()?.split('?')[0]?.toLowerCase() || '';

  if (!normalizedContent) return true;
  if (normalizedContent === fileName || normalizedContent === fileUrlName) return true;
  return /^audio-\d+\.(webm|ogg|mp3|wav|m4a)$/i.test(normalizedContent);
}

function getAudioTranscription(message: ChatbotMessage): string | null {
  if (getMessageKind(message) !== 'audio') {
    return null;
  }

  const contextText = message.contextContent?.trim();
  if (contextText) {
    return contextText;
  }

  const content = message.content.trim();
  if (isAudioPlaceholderContent(message, content)) {
    return null;
  }
  return content || null;
}

function renderAiContextBlock(message: ChatbotMessage): React.ReactNode {
  const contextText = message.contextContent?.trim();
  if (!contextText) {
    return null;
  }

  return (
    <details className='mt-2 rounded border border-dashed p-2 text-xs opacity-90'>
      <summary className='cursor-pointer font-medium'>Contexto IA (exportación / admin)</summary>
      <div className='mt-2 whitespace-pre-wrap'>{contextText}</div>
    </details>
  );
}

function getConversationTitle(conversation: ChatbotConversation): string {
  return conversation.userLabel || conversation.userIdentifier || conversation.userId || 'Usuario';
}

function getConversationSubtitle(
  conversation: ChatbotConversation,
  isSuperAdmin: boolean
): string {
  if (isSuperAdmin && conversation.tenantName) {
    return `${conversation.botName} · ${conversation.tenantName}`;
  }
  return conversation.botName;
}

function formatMessageDateTime(value: string): string {
  return format(new Date(value), 'dd/MM/yyyy HH:mm');
}

export function ConversationViewer({
  instanceId,
  open,
  onOpenChange,
  canExport = false,
}: ConversationViewerProps) {
  const [conversations, setConversations] = useState<ChatbotConversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<ChatbotConversation | null>(
    null
  );
  const [messages, setMessages] = useState<ChatbotMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [deletingConversation, setDeletingConversation] = useState(false);
  const [userInfoFields, setUserInfoFields] = useState<ChatbotInfoField[]>(padChatbotInfoFields([]));
  const [userInfoEnabled, setUserInfoEnabled] = useState(false);
  const [savingUserInfo, setSavingUserInfo] = useState(false);
  const currentUser = useAppSelector(selectCurrentUser);
  const isSuperAdmin = currentUser?.role === 'SUPERADMIN';

  useEffect(() => {
    if (open && instanceId) {
      loadConversations();
    }
  }, [open, instanceId]);

  useEffect(() => {
    if (selectedConversation) {
      loadMessages(selectedConversation);
      loadUserInfoFields(selectedConversation);
    }
  }, [selectedConversation]);

  const loadConversations = async () => {
    try {
      const data = await chatbotAdminApi.listAllConversations(instanceId, 100);
      setConversations(data);
      if (data.length === 0) {
        setSelectedConversation(null);
        setMessages([]);
        return;
      }
      setSelectedConversation(current => {
        if (current && data.some(conv => conv.id === current.id)) return current;
        return data[0];
      });
    } catch (error) {
      console.error('Error loading conversations', error);
    }
  };

  const loadMessages = async (conversation: ChatbotConversation) => {
    try {
      setLoading(true);
      const messageLimit = Math.max(conversation.messageCount ?? 0, 5000);
      const data = await chatbotAdminApi.getConversationMessages(
        instanceId,
        conversation.id,
        messageLimit
      );
      setMessages(data);
    } catch (error) {
      console.error('Error loading messages', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUserInfoFields = async (conversation: ChatbotConversation) => {
    if (!conversation.userInfoFieldsEnabled) {
      setUserInfoEnabled(false);
      setUserInfoFields(padChatbotInfoFields([]));
      return;
    }
    try {
      const data = await chatbotAdminApi.getConversationInfoFields(instanceId, conversation.id);
      setUserInfoEnabled(data.enabled);
      setUserInfoFields(padChatbotInfoFields(data.infoFields));
    } catch {
      setUserInfoEnabled(false);
      setUserInfoFields(padChatbotInfoFields([]));
    }
  };

  const handleSaveUserInfo = async () => {
    if (!selectedConversation || savingUserInfo) {
      return;
    }
    setSavingUserInfo(true);
    try {
      const saved = await chatbotAdminApi.updateConversationInfoFields(
        instanceId,
        selectedConversation.id,
        compactChatbotInfoFields(userInfoFields)
      );
      setUserInfoFields(padChatbotInfoFields(saved.infoFields));
      toast.success('Info de este contacto guardada');
    } catch {
      toast.error('Error al guardar info del contacto');
    } finally {
      setSavingUserInfo(false);
    }
  };

  const downloadBlob = (blob: Blob, fileName: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleExport = async (formatType: ExportFormat) => {
    if (!selectedConversation) return;
    try {
      const blob = await chatbotAdminApi.exportSingleConversation(
        instanceId,
        selectedConversation.id,
        formatType
      );
      downloadBlob(blob, `conversation-${selectedConversation.id}.${formatType.toLowerCase()}`);
    } catch (error) {
      toast.error('Error al exportar conversación');
    }
  };

  const handleBotExport = async (formatType: ExportFormat) => {
    if (!selectedConversation) return;
    try {
      const blob = await chatbotAdminApi.exportConversations(
        instanceId,
        formatType,
        selectedConversation.botId
      );
      downloadBlob(
        blob,
        `bot-${selectedConversation.botId}-conversations.${formatType.toLowerCase()}`
      );
    } catch (error) {
      toast.error('Error al exportar conversaciones del bot');
    }
  };

  const handleDeleteConversation = async () => {
    if (!selectedConversation || deletingConversation) return;
    const confirmed = window.confirm(
      '¿Eliminar esta conversación completa? Se borrarán los mensajes, adjuntos y el historial de la conversación.'
    );
    if (!confirmed) return;

    try {
      setDeletingConversation(true);
      await chatbotAdminApi.deleteConversation(instanceId, selectedConversation.id);
      toast.success('Conversación eliminada');

      const remaining = conversations.filter(conv => conv.id !== selectedConversation.id);
      setConversations(remaining);
      setMessages([]);
      setSelectedConversation(remaining[0] || null);
    } catch (error) {
      toast.error('Error al eliminar conversación');
    } finally {
      setDeletingConversation(false);
    }
  };

  const renderMessageContent = (msg: ChatbotMessage) => {
    const kind = getMessageKind(msg);

    if (kind === 'text') {
      return <div>{msg.content}</div>;
    }

    if (kind === 'image' && msg.fileUrl) {
      return (
        <AuthenticatedMedia url={msg.fileUrl}>
          {src => (
            <div className='space-y-2'>
              <img
                src={src}
                alt={getFileName(msg)}
                className='max-h-80 rounded-md border object-contain'
                loading='lazy'
              />
              <a
                href={src}
                target='_blank'
                rel='noopener noreferrer'
                className='block text-xs underline'
              >
                Ver original / descargar
              </a>
              {renderAiContextBlock(msg)}
            </div>
          )}
        </AuthenticatedMedia>
      );
    }

    if (kind === 'audio' && msg.fileUrl) {
      return (
        <AuthenticatedMedia url={msg.fileUrl}>
          {src => (
            <div className='space-y-2'>
              <audio controls className='w-full' src={src}>
                <a href={src}>Descargar audio</a>
              </audio>
              <a href={src} download className='block text-xs underline'>
                Descargar audio
              </a>
            </div>
          )}
        </AuthenticatedMedia>
      );
    }

    if (!msg.fileUrl) return null;
    const fileSize = formatFileSize(getMetadataNumber(msg.fileMetadata?.size));
    const hasContent = msg.content.trim().length > 0;
    return (
      <AuthenticatedMedia url={msg.fileUrl}>
        {src => (
          <div className='rounded-md border bg-background/50 p-3'>
            <div className='font-medium'>{getFileName(msg)}</div>
            {fileSize && <div className='text-xs opacity-70'>{fileSize}</div>}
            {hasContent && <div className='mt-2 text-sm'>{msg.content}</div>}
            {renderAiContextBlock(msg)}
            <a href={src} download className='mt-2 block text-xs underline'>
              Descargar archivo
            </a>
          </div>
        )}
      </AuthenticatedMedia>
    );
  };

  const renderTranscriptionBubble = (msg: ChatbotMessage) => {
    const transcription = getAudioTranscription(msg);
    if (!transcription) return null;

    return (
      <div className={`flex ${msg.sender === 'USER' ? 'justify-end' : 'justify-start'}`}>
        <div
          className={`max-w-[80%] p-3 rounded-lg ${
            msg.sender === 'USER' ? 'bg-primary text-primary-foreground' : 'bg-muted'
          }`}
        >
          <div>{transcription}</div>
          <div className='text-xs opacity-70 mt-1'>
            {formatMessageDateTime(msg.createdAt)}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-4xl h-[80vh] flex flex-col'>
        <DialogHeader>
          <DialogTitle>Visor de Conversaciones</DialogTitle>
        </DialogHeader>
        <div className='flex flex-1 overflow-hidden gap-4'>
          <div className='w-1/3 border-r pr-4 overflow-y-auto'>
            {conversations.map(conv => (
              <div
                key={conv.id}
                className={`p-3 cursor-pointer hover:bg-muted rounded-lg mb-2 ${selectedConversation?.id === conv.id ? 'bg-muted' : ''}`}
                onClick={() => setSelectedConversation(conv)}
              >
                <div className='font-medium'>{getConversationTitle(conv)}</div>
                <div className='text-xs text-muted-foreground'>
                  {getConversationSubtitle(conv, isSuperAdmin)}
                </div>
                <div className='text-xs text-muted-foreground'>
                  Último mensaje: {formatMessageDateTime(conv.lastMessageAt)}
                </div>
                <div className='text-xs text-muted-foreground'>
                  {conv.messageCount ?? 0} mensaje{(conv.messageCount ?? 0) === 1 ? '' : 's'}
                </div>
                <div className='text-sm truncate'>{conv.lastMessagePreview || 'Sin mensajes'}</div>
              </div>
            ))}
          </div>
          <div className='flex-1 flex flex-col'>
            {selectedConversation ? (
              <>
                <div className='flex flex-wrap justify-end gap-2 mb-2'>
                  {canExport && (
                    <>
                      <Button size='sm' variant='outline' onClick={() => handleExport('CSV')}>
                        <FileText className='h-4 w-4 mr-2' /> CSV
                      </Button>
                      <Button size='sm' variant='outline' onClick={() => handleExport('JSON')}>
                        <FileJson className='h-4 w-4 mr-2' /> JSON
                      </Button>
                      <Button size='sm' variant='outline' onClick={() => handleExport('PDF')}>
                        <FileText className='h-4 w-4 mr-2' /> PDF
                      </Button>
                      <select
                        title='Exportar conversaciones del bot'
                        className='border border-input bg-background text-foreground rounded-md px-2 text-xs'
                        defaultValue=''
                        onChange={event => {
                          const formatType = event.target.value as ExportFormat;
                          event.target.value = '';
                          if (formatType) handleBotExport(formatType);
                        }}
                      >
                        <option value='' disabled>
                          Exportar bot
                        </option>
                        <option value='CSV'>Bot CSV</option>
                        <option value='JSON'>Bot JSON</option>
                        <option value='PDF'>Bot PDF</option>
                      </select>
                    </>
                  )}
                  <Button size='sm' variant='outline' onClick={handleDeleteConversation}>
                    <Trash2 className='h-4 w-4 mr-2' />
                    {deletingConversation ? 'Eliminando...' : 'Eliminar'}
                  </Button>
                </div>
                <div className='flex-1 border rounded-lg p-4 overflow-y-auto'>
                  {userInfoEnabled && (
                    <div className='mb-4 rounded-md border p-3 space-y-3'>
                      <div className='text-sm font-medium'>Info de este contacto en este bot</div>
                      <p className='text-xs text-muted-foreground'>
                        Solo aplica a esta persona con este bot. El próximo mensaje ya los usa.
                      </p>
                      <InfoFieldsEditor
                        idPrefix='conv-user'
                        fields={userInfoFields}
                        onChange={setUserInfoFields}
                      />
                      <div className='flex justify-end'>
                        <Button size='sm' onClick={handleSaveUserInfo} disabled={savingUserInfo}>
                          {savingUserInfo ? 'Guardando...' : 'Guardar info'}
                        </Button>
                      </div>
                    </div>
                  )}
                  {loading ? (
                    <div className='text-center'>Cargando...</div>
                  ) : (
                    <div className='space-y-4'>
                      {messages.map(msg => (
                        <div key={msg.id} className='space-y-2'>
                          <div
                            className={`flex ${msg.sender === 'USER' ? 'justify-end' : 'justify-start'}`}
                          >
                            <div
                              className={`max-w-[80%] p-3 rounded-lg ${
                                msg.sender === 'USER'
                                  ? 'bg-primary text-primary-foreground'
                                  : 'bg-muted'
                              }`}
                            >
                              {renderMessageContent(msg)}
                              <div className='text-xs opacity-70 mt-1'>
                                {formatMessageDateTime(msg.createdAt)}
                              </div>
                            </div>
                          </div>
                          {renderTranscriptionBubble(msg)}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className='flex items-center justify-center h-full text-muted-foreground'>
                Selecciona una conversación
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
